import React from "react";
import {
  Row,
  Col,
  Typography,
  Tag,
  Tooltip,
  Button as AntButton,
  message,
} from "antd";
import {
  ExportOutlined,
  EditOutlined,
  CopyOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { Send } from "react-feather";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

const OrderProductList = ({ products, onProductSelect, isDarkMode = false }) => {
  const { t } = useTranslation();
  if (!products || products.length === 0) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          color: "#8c8c8c",
          width: "100%",
        }}
      >
        {t("orderDetails.noProductsFound", "No products found in this order.")}
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .product-card-container {
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid #e8e8e8;
            transition: all 0.3s ease;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          }
          .product-card-container:hover {
            transform: translateY(-3px);
            box-shadow: 0 6px 16px rgba(0,0,0,0.09);
          }
          .product-card-container:hover .overlay-actions {
            opacity: 1;
          }
          .overlay-actions {
            opacity: 0;
            transition: opacity 0.3s ease;
            background: rgba(0, 0, 0, 0.45);
            backdrop-filter: blur(2px);
          }
          .product-title-clamp {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.4;
            min-height: 2.8em;
          }
        `}
      </style>

      <Row gutter={[12, 12]} style={{ width: "100%" }}>
        {products.map((item, i) => {
          const product = item?.variation?.product;
          const variation = item?.variation;
          const imageUrl = `${process.env.REACT_APP_PUBLIC_MEDIA_URL}store/${variation?.store}/${variation?.images}`;
          const webUrl = `${process.env.REACT_APP_FRONTEND_URL || "https://www.wege.ae"}/en/products/${product?.id}`;
          const alibabaUrl =
            product?.link ||
            (product?.offer_id
              ? `https://detail.1688.com/offer/${product.offer_id}.html`
              : null);
          const sizeName =
            variation?.size?.name_eu || variation?.size?.name || "N/A";
          const unitPrice = Number(item?.price || variation?.price || 0).toFixed(2);
          const subtotal = Number(
            item?.subtotal !== undefined
              ? item.subtotal
              : Number(unitPrice) * (item?.quantity || 1)
          ).toFixed(2);

          return (
            <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={4} key={item.id || i}>
              <div
                className="product-card-container"
                style={{
                  background: isDarkMode ? "#1f1f1f" : "#fff",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {/* Image & Quick Action Overlay */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: 150,
                    overflow: "hidden",
                    backgroundColor: isDarkMode ? "#141414" : "#f9f9f9",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (onProductSelect) {
                      onProductSelect(item);
                    }
                  }}
                >
                  <img
                    src={imageUrl}
                    alt={product?.name || "Product"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        "https://placehold.co/300x300?text=No+Image";
                    }}
                  />

                  {/* Quantity badge top-right */}
                  <span
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "#1677ff",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 12,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    ×{item?.quantity || 1}
                  </span>

                  {/* Hover Actions */}
                  <div
                    className="overlay-actions"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      zIndex: 2,
                    }}
                  >
                    <Tooltip title={t("orderDetails.viewDetails", "View Details")}>
                      <AntButton
                        shape="circle"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onProductSelect) onProductSelect(item);
                        }}
                      />
                    </Tooltip>

                    <Tooltip title={t("orderDetails.openWebsite", "Open Website")}>
                      <AntButton
                        shape="circle"
                        type="primary"
                        icon={<ExportOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(webUrl, "_blank");
                        }}
                      />
                    </Tooltip>

                    {alibabaUrl && (
                      <Tooltip title={t("orderDetails.open1688Listing", "Open 1688 Listing")}>
                        <AntButton
                          shape="circle"
                          style={{
                            background: "#ff7700",
                            borderColor: "#ff7700",
                            color: "#fff",
                          }}
                          icon={<Send size={13} style={{ transform: "rotate(45deg)" }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(alibabaUrl, "_blank");
                          }}
                        />
                      </Tooltip>
                    )}

                    <Tooltip title={t("orderDetails.editProduct", "Edit Product")}>
                      <Link
                        to={`/products/physical/update/${product?.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <AntButton shape="circle" icon={<EditOutlined />} />
                      </Link>
                    </Tooltip>
                  </div>
                </div>

                {/* Content */}
                <div
                  style={{
                    padding: "10px",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    {/* Title */}
                    <Tooltip title={product?.name}>
                      <Link
                        to={`/products/physical/update/${product?.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ textDecoration: "none" }}
                      >
                        <Text
                          strong
                          className="product-title-clamp"
                          style={{
                            color: isDarkMode ? "#e6e6e6" : "#262626",
                            fontSize: 12,
                            marginBottom: 6,
                          }}
                        >
                          {product?.name || "Product Name"}
                        </Text>
                      </Link>
                    </Tooltip>

                    {/* Variation Tags */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        flexWrap: "wrap",
                        marginBottom: 6,
                      }}
                    >
                      {variation?.color?.name && (
                        <Tag
                          color="default"
                          style={{
                            margin: 0,
                            fontSize: 10,
                            padding: "0 4px",
                            borderRadius: 4,
                            background: isDarkMode ? "#262626" : "#f5f5f5",
                          }}
                        >
                          {variation.color.name}
                        </Tag>
                      )}
                      <Tag
                        color="blue"
                        style={{
                          margin: 0,
                          fontSize: 10,
                          padding: "0 4px",
                          borderRadius: 4,
                        }}
                      >
                        {sizeName}
                      </Tag>
                    </div>

                    {/* SKU info */}
                    {variation?.wege_sku && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 8,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: 11,
                            color: "#8c8c8c",
                          }}
                        >
                          {variation.wege_sku}
                        </span>
                        <Tooltip title={t("orderDetails.copySku", "Copy SKU")}>
                          <CopyOutlined
                            style={{
                              fontSize: 11,
                              cursor: "pointer",
                              color: "#1677ff",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(variation.wege_sku);
                              message.success(t("orderDetails.skuCopied", "SKU copied!"));
                            }}
                          />
                        </Tooltip>
                      </div>
                    )}
                  </div>

                  {/* Price & Subtotal Footer */}
                  <div
                    style={{
                      borderTop: isDarkMode
                        ? "1px solid #303030"
                        : "1px solid #f0f0f0",
                      paddingTop: 8,
                      marginTop: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                        {t("orderDetails.unitPrice", "Price:")}{" "}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 500 }}>
                        {unitPrice} AED
                      </span>
                    </div>
                    <div>
                      <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                        {t("orderDetails.subtotal", "Subtotal:")}{" "}
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#1677ff",
                        }}
                      >
                        {subtotal} AED
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </>
  );
};

export default OrderProductList;
